import { useCallback, useEffect, useRef, useState } from 'react'

type Options = {
  /** Zo lang na de laatste wijziging wordt hij pas verstuurd (ms). */
  delay?: number
  /** Mag er nu verstuurd worden? Zo niet, dan wacht hij en probeert het daarna opnieuw. */
  canCommit?: () => boolean
}

/**
 * Een waarde die direct op het scherm verandert, maar pas na een korte rust één keer wordt
 * bewaard. Voor min/plus-knoppen: drie snelle tikken op "+" worden één verzoek met de
 * eindstand, in plaats van één verzoek waarna de andere twee tikken wegvallen.
 *
 * - `external` is de bewaarde stand (bijv. uit het klantdossier). Zolang er niets wacht, toont
 *   de hook die, zodat een terugdraai na een fout gewoon zichtbaar wordt.
 * - Komt de waarde terug op de bewaarde stand (+ en daarna −), dan wordt er niets verstuurd.
 * - Verlaat je het scherm met een wachtende wijziging, dan wordt hij alsnog verstuurd.
 */
export function useBufferedCommit<T>(external: T, commit: (value: T) => void, { delay = 450, canCommit }: Options = {}) {
  const [draft, setDraft] = useState<{ value: T } | null>(null)
  const pending = useRef<{ value: T } | null>(null)
  const timer = useRef<number | undefined>(undefined)

  // Altijd de nieuwste versies, zonder dat de timer opnieuw moet starten.
  const latest = useRef({ external, commit, canCommit, delay })
  latest.current = { external, commit, canCommit, delay }

  const send = useCallback((fromUnmount: boolean) => {
    window.clearTimeout(timer.current)
    const p = pending.current
    if (!p) return
    const { external: saved, commit: save, canCommit: can, delay: wait } = latest.current
    if (!fromUnmount && can && !can()) {
      timer.current = window.setTimeout(() => send(false), wait)
      return
    }
    pending.current = null
    if (!fromUnmount) setDraft(null)
    if (!Object.is(p.value, saved)) save(p.value)
  }, [])

  const set = useCallback(
    (value: T) => {
      pending.current = { value }
      setDraft({ value })
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => send(false), latest.current.delay)
    },
    [send],
  )

  useEffect(() => () => send(true), [send])

  return [draft ? draft.value : external, set, draft !== null] as const
}
